import { toast } from 'react-toastify';
import appApi from '../../../../api/app-api';
import { displayErrorText } from '../../../../api/errors/api-error-codes';
import { AccountingDocumentEntity } from '../../../../api/types/accounting/accounting-documents-entity';

const extractFilenameFromContentDisposition = (
  contentDisposition: string | undefined,
): string | null => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ''));

  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (match?.[1]) return match[1];

  return null;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

const buildFileName = (count: number): string => {
  const today = new Date();
  const datePart = `${String(today.getDate()).padStart(2, '0')}-${String(
    today.getMonth() + 1,
  ).padStart(2, '0')}-${today.getFullYear()}`;
  return `מסמכים חשבונאיים (${count}) - ${datePart}.pdf`;
};

// Backend may return a GenericResponse JSON (with errCode) under HTTP 200 even
// when we asked for a blob — detect that before treating the body as a PDF.
const tryParseGenericError = async (
  blob: Blob,
): Promise<{ errCode?: string } | null> => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && 'errCode' in parsed) {
      return parsed as { errCode?: string };
    }
    return null;
  } catch {
    return null;
  }
};

export const exportAccountingDocumentsPdfOperation = async (
  selectedDocuments: AccountingDocumentEntity[],
  setExporting: (exporting: boolean) => void,
) => {
  try {
    if (!selectedDocuments || selectedDocuments.length === 0) {
      toast.warn('יש לבחור לפחות מסמך אחד לייצוא');
      return;
    }

    const documentIds = selectedDocuments
      .map((d) => d.id)
      .filter((id): id is number => typeof id === 'number' && id > 0);

    if (documentIds.length === 0) {
      toast.warn('למסמכים שנבחרו אין מזהים תקינים לייצוא');
      return;
    }

    setExporting(true);

    const res = await appApi.mergeAccountingDocumentsPdf(documentIds);
    const blob = res.data;
    const contentType = String(res.headers?.['content-type'] ?? '');

    // Backend returns GenericResponse JSON (with errCode) on business errors,
    // otherwise a raw PDF blob.
    if (
      contentType.includes('application/json') ||
      blob.type.includes('json')
    ) {
      const maybeErr = await tryParseGenericError(blob);
      if (maybeErr?.errCode) {
        toast.warn(displayErrorText(maybeErr.errCode));
        setExporting(false);
        return;
      }
      toast.error('לא ניתן היה לייצר את קובץ ה-PDF');
      setExporting(false);
      return;
    }

    // Guard against empty / non-PDF payloads (e.g. unexpected HTML error pages).
    if (!blob || blob.size === 0) {
      toast.error('לא ניתן היה לייצר את קובץ ה-PDF');
      setExporting(false);
      return;
    }

    const filenameFromHeader = extractFilenameFromContentDisposition(
      res.headers?.['content-disposition'],
    );
    const filename =
      filenameFromHeader || buildFileName(selectedDocuments.length);

    downloadBlob(blob, filename);
    toast.success('המסמכים אוחדו והורדו בהצלחה');
    setExporting(false);
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setExporting(false);
  }
};
